import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * SEO Component - Dynamically injects SEO meta tags from settings
 */
const SEOHead = () => {
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    const fetchSEO = async () => {
      try {
        const response = await axios.get(`${API}/seo`);
        setSeo(response.data);
      } catch (error) {
        console.error('Failed to fetch SEO settings:', error);
      }
    };
    fetchSEO();
  }, []);

  useEffect(() => {
    if (!seo) return;

    // Update document title
    if (seo.seo_title) {
      document.title = seo.seo_title;
    }

    // Helper to update or create meta tag
    const updateMeta = (name, content, property = false) => {
      if (!content) return;
      
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update meta tags
    updateMeta('description', seo.seo_description);
    updateMeta('keywords', seo.seo_keywords);
    // /bedankt is always noindex (managed by ThankYouPage)
    if (!window.location.pathname.startsWith('/bedankt')) {
      updateMeta('robots', seo.seo_robots);
    }
    updateMeta('google-site-verification', seo.seo_google_verification);
    
    // Open Graph
    updateMeta('og:title', seo.seo_title, true);
    updateMeta('og:description', seo.seo_description, true);
    updateMeta('og:type', seo.seo_og_type, true);
    updateMeta('og:image', seo.seo_og_image, true);
    
    // Twitter Card
    updateMeta('twitter:card', seo.seo_twitter_card);
    updateMeta('twitter:title', seo.seo_title);
    updateMeta('twitter:description', seo.seo_description);
    updateMeta('twitter:image', seo.seo_og_image);

    // Canonical URL
    if (seo.seo_canonical_url) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', seo.seo_canonical_url);
    }

    // Favicon
    if (seo.seo_favicon) {
      let favicon = document.querySelector('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.setAttribute('rel', 'icon');
        document.head.appendChild(favicon);
      }
      favicon.setAttribute('href', seo.seo_favicon);
    }

    // Structured Data / JSON-LD
    if (seo.seo_structured_data) {
      try {
        // Validate JSON
        JSON.parse(seo.seo_structured_data);
        
        let script = document.querySelector('script[type="application/ld+json"]');
        if (!script) {
          script = document.createElement('script');
          script.setAttribute('type', 'application/ld+json');
          document.head.appendChild(script);
        }
        script.textContent = seo.seo_structured_data;
      } catch (e) {
        console.error('Invalid JSON-LD:', e);
      }
    }
  }, [seo]);

  return null; // This component only manages document head
};

export default SEOHead;
